import { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { computePremiumFlow, markRollClosures, type FlowPosition } from '@steward/shared';
import type { PositionWithLegs } from '@steward/shared';
import { PremiumFlowChart } from '@/components/analytics/PremiumFlowChart';

interface Props {
  positions: PositionWithLegs[];
  underlyingSymbol: string;
  chainId: string;
}

export function ChainPremiumFlow({ positions, underlyingSymbol, chainId }: Props) {
  const flow = useMemo(() => {
    const closedByRoll = markRollClosures(positions);
    const input: { positions: FlowPosition[] } = {
      positions: positions.map((p) => ({
        chainId,
        symbol: underlyingSymbol,
        netPremium: p.netPremium,
        status: p.status,
        closeReason: p.closeReason,
        closedByRoll: closedByRoll.has(p.id),
        legs: p.legs.map((l) => ({
          action: l.action,
          quantity: l.quantity,
          quantityRemaining: l.quantityRemaining,
          pricePerContract: l.pricePerContract,
          closeReason: l.closeReason,
          transactions: (l.transactions ?? []).map((t) => ({
            transactionType: t.transactionType,
            quantity: t.quantity,
            pricePerContract: t.pricePerContract,
          })),
        })),
      })),
    };
    return computePremiumFlow(input);
  }, [positions, underlyingSymbol, chainId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Premium Flow</CardTitle>
      </CardHeader>
      <CardContent>
        {flow.collected === 0 ? (
          <div className="h-60 flex items-center justify-center">
            <EmptyState icon={<NoDataIcon />} title="No premium collected" description="This chain has no collected premium to trace" />
          </div>
        ) : (
          <PremiumFlowChart flow={flow} />
        )}
      </CardContent>
    </Card>
  );
}
